import MapEditType from "@/constant/MapElementType";
import ChildTaskStatus from "@/model/ChildTaskStatus";
import { SubTask } from "@/model/CleanTaskingInfo";
import HeadingPoint from "@/model/HeadingPoint";
import LocLaneInfo from "@/model/LocLaneInfo";
import LocMapElement2 from "@/model/LocMapElement2";
import MapBaseInfo from "@/model/MapBaseInfo";
import VehicleMapInfo from "@/model/VehicleMapInfo";
import {
  AmbientLight,
  Cache,
  FrontSide,
  Group,
  Line,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  Texture,
  TextureLoader,
  WebGLRenderer,
} from "three";
import { Line2 } from "three/examples/jsm/lines/Line2";
import {
  createAccessLines,
  createCarIcon,
  createFuncAreas,
  createFuncAreasTextGroups,
  createFuncPoints,
  createGriddingScaleLine,
  createLaneInfoMeshImg,
  createNoiseAreas,
  createVirtualWalls,
} from "./WebGLManagerUtils";

const MapPicRenderOrder = 0; //地图图片位置的层级
const connectAnchorLineOrder = 5;
const VirtualWallsLineOrder = 2;
const AccessControlLineOrder = 2;
const PathLinesOrder = 2;
const TaskPathRenderOrder = 5; //
const TaskingPathRenderOrder = 6; //
const FuncAreaOrder = 7;
const carLinkLineOrder = 8;
const GriddingScaleOrder = 8;
const FuncPointRenderOrder = 9; //
const CarRenderOrder = 10; //小车位置的层级
const TheTopOrder = 11; //最顶层
const CloudPointsOrder = 12; //点云层级
const FillHandPaintedOrder = 8.5; //手绘填充的层级

export default class WebGlManager {
  private canvasDivId: string;
  private canvasDiv: HTMLDivElement | null;

  private translateX = 0;
  private translateY = 0;
  private scale = 1;
  private rotateAngle = 0;

  private left_boundary?: number;
  private down_boundary?: number;
  private mapPicWidth?: number;
  private mapPicHeight?: number;

  private carPosition?: HeadingPoint;

  private showFuncPointsType = -1; //是否允显示地图所有功能点

  private showGriddingScale = false; //是否显示网格比例尺
  private griddingScaleValue = 0;
  private griddingScaleWidth = 0;
  private griddingScaleHeight = 0;

  private mapNoiseAreas?: Array<LocMapElement2> | null;
  private mapVirtualWalls?: Array<LocMapElement2> | null;
  private mapAccessControlLine?: Array<LocMapElement2> | null;
  private functionPoints?: Array<LocMapElement2> | null;
  private functionAreas?: Array<LocMapElement2> | null;
  private canShowFunctionPoints?: Array<LocMapElement2> | null;

  private showCar = false;
  private showVirtualWalls = false;
  private showFunctionPoints = false;
  private showFunctionAreas = false;

  private glScene?: Scene;
  private glCamera?: OrthographicCamera;
  private ambientLight?: AmbientLight;
  private glRender?: WebGLRenderer | null;
  private glGroup?: Group | null;
  private onlyScaleGroup?: Group;
  private staticGroup?: Group;
  private mapPictexture?: Texture;
  private mapPicMesh?: Mesh | null; //地图图片Mesh
  private carIconMesh?: Mesh | null;
  private griddingLineGroup?: Group | null;
  private virtualWallsLine?: Line2[] | null;
  private accessControlLine?: Group[] | null;
  private noiseAreasMesh?: Mesh[] | null;
  private funcAreasMesh?: Array<Mesh | Line | Line2> | null;
  private funcAreasTextGroups?: Group[] | null;
  private funcPointsGroup?: Group[] | null;
  private runningTaskPathsMap = new Map<number, Mesh>(); //地图图片Mesh
  private showCarIcon = true;

  constructor(parentDivId: string) {
    this.canvasDiv = null;
    this.canvasDivId = parentDivId;
  }

  private initDomElement = (): void => {
    this.canvasDiv = document.getElementById(
      this.canvasDivId
    ) as HTMLDivElement;
  };

  private initWebGL = (): void => {
    this.glScene = new Scene();
    this.glGroup = new Group();
    this.staticGroup = new Group();
    this.staticGroup.renderOrder = TheTopOrder;
    this.onlyScaleGroup = new Group();
    const w = this.canvasDiv?.clientWidth as number;
    const h = this.canvasDiv?.clientHeight as number;
    //创建相机对象
    this.glCamera = new OrthographicCamera(
      -w / 2,
      w / 2,
      h / 2,
      -h / 2,
      0.1,
      1000
    );
    this.glCamera.position.z = 1;
    this.glRender = new WebGLRenderer({ antialias: true });
    this.glRender.setPixelRatio(window.devicePixelRatio);
    this.glRender.setSize(w, h);
    this.glRender.setClearColor("#fff", 1.0);
    this.canvasDiv?.appendChild(this.glRender.domElement);
    this.ambientLight = new AmbientLight(0xffffff); // 环境光
    this.glScene.add(this.ambientLight);
    this.glScene.add(this.glGroup);
    this.glScene.add(this.staticGroup);
    this.glScene.add(this.onlyScaleGroup);
    this.glRender.render(this.glScene, this.glCamera);
  };

  init = (): void => {
    this.initDomElement();
    this.initWebGL();
  };

  cleanLastMapData = (): void => {
    this.clearAllMapElements();
    this.clearRunningTaskPaths();
  };

  setMapPicInfo = (
    mapBean: VehicleMapInfo,
    loadBack: (mapBaseInfo: MapBaseInfo) => void
  ): void => {
    this.left_boundary = mapBean.result.left_up_coordinate.xg;
    this.down_boundary = mapBean.result.left_up_coordinate.yg;
    const picStr = mapBean.result.map_url;
    const textureLoader = new TextureLoader();
    textureLoader.load(picStr, (texture) => {
      this.cleanLastMapData();
      console.log("texture图片base64加载成功 ");
      if (this.mapPictexture) {
        this.mapPictexture.dispose();
      }
      this.mapPictexture = texture;
      this.clearLastMapPic();
      const material = new MeshBasicMaterial({
        map: texture,
        side: FrontSide,
      });
      this.mapPicWidth = texture.image.width as number;
      this.mapPicHeight = texture.image.height as number;
      const geometry = new PlaneGeometry(this.mapPicWidth, this.mapPicHeight);
      this.mapPicMesh = new Mesh(geometry, material);
      this.mapPicMesh.renderOrder = MapPicRenderOrder;
      if (this.glGroup) {
        this.glGroup.add(this.mapPicMesh);
        this.renderView();
      }
      const mapBaseInfo = {} as MapBaseInfo;
      mapBaseInfo.left_boundary = mapBean.result.left_up_coordinate.xg;
      mapBaseInfo.down_boundary = mapBean.result.left_up_coordinate.yg;
      mapBaseInfo.map_width = this.mapPicWidth;
      mapBaseInfo.map_height = this.mapPicHeight;
      if (loadBack) {
        loadBack(mapBaseInfo);
      }
      this.setGriddingScaleWH();
    });
  };

  private isAddCarMesh = false;
  addCarIconMesh = (): void => {
    if (!this.isAddCarMesh && this.carPosition) {
      this.isAddCarMesh = true;
      this.carIconMesh = createCarIcon();
      if (this.carIconMesh) {
        this.carIconMesh.scale.setX(1 / this.scale);
        this.carIconMesh.scale.setY(1 / this.scale);
        this.carIconMesh.position.setX(this.carPosition.x);
        this.carIconMesh.position.setY(this.carPosition.y);
        this.carIconMesh.renderOrder = CarRenderOrder;
      }
    }
  };

  setCarMeshPosition = (): void => {
    if (this.carIconMesh && this.carPosition) {
      this.carIconMesh.position.setX(this.carPosition.x);
      this.carIconMesh.position.setY(this.carPosition.y);
      const carRotate = ((this.carPosition?.heading as number) * Math.PI) / 180;
      this.carIconMesh.rotation.z = carRotate;
    }
  };

  private handleShowIconMesh = () => {
    if (this.carIconMesh && this.glGroup) {
      if (this.showCarIcon) {
        this.glGroup.add(this.carIconMesh);
      } else {
        this.glGroup.remove(this.carIconMesh);
      }
    }
  };

  setShowCarIcon = (show: boolean): void => {
    this.showCarIcon = show;
    this.setShowCarStatus();
  };

  private setShowCarStatus = (): void => {
    this.addCarIconMesh();
    this.handleShowIconMesh();
    this.setCarMeshPosition();
    this.renderView();
  };

  setCarIconScale = (): void => {
    if (this.carIconMesh) {
      this.carIconMesh.scale.setX(1 / this.scale);
      this.carIconMesh.scale.setY(1 / this.scale);
    }
  };

  clearCarIconMesh = (): void => {
    if (this.carIconMesh && this.glGroup) {
      this.glGroup.remove(this.carIconMesh);
      this.carIconMesh?.geometry?.dispose();
      this.carIconMesh?.clear();
    }
  };

  setCarPosition = (position: HeadingPoint): void => {
    this.carPosition = position;
    this.setShowCarStatus();
  };

  clearVirtualWallsLine = (): void => {
    if (this.virtualWallsLine && this.virtualWallsLine?.length > 0) {
      this.virtualWallsLine.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
        val?.geometry?.dispose();
        val?.clear();
      });
    }
  };

  clearAccessControlLine = (): void => {
    if (this.accessControlLine && this.accessControlLine?.length > 0) {
      this.accessControlLine.forEach((group) => {
        group?.traverse((obj) => {
          obj?.clear();
        });
        if (this.glGroup) {
          this.glGroup.remove(group);
        }
        group.clear();
      });
    }
  };

  clearNoiseAreas = (): void => {
    if (this.noiseAreasMesh && this.noiseAreasMesh?.length > 0) {
      this.noiseAreasMesh.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
        val?.geometry?.dispose();
        val.clear();
      });
    }
  };

  clearFuncAreas = (): void => {
    if (this.funcAreasMesh && this.funcAreasMesh?.length > 0) {
      this.funcAreasMesh.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
        val?.geometry?.dispose();
        val.clear();
      });
    }
  };

  clearFuncAreasText = (): void => {
    if (this.funcAreasTextGroups && this.funcAreasTextGroups?.length > 0) {
      this.funcAreasTextGroups.forEach((val) => {
        val?.traverse((obj) => {
          obj?.clear();
        });
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
        val.clear();
      });
    }
  };

  clearFuncPoints = (): void => {
    if (this.funcPointsGroup && this.funcPointsGroup?.length > 0) {
      this.funcPointsGroup.forEach((group) => {
        group?.traverse((obj) => {
          obj?.clear();
        });
        if (this.glGroup) {
          this.glGroup.remove(group);
        }
        group.clear();
      });
      this.funcPointsGroup = [];
    }
  };

  setFuncAreasTextScale = (): void => {
    if (this.showFunctionAreas) {
      this.funcAreasTextGroups?.forEach((val) => {
        val.scale.setX(1 / this.scale);
        val.scale.setY(1 / this.scale);
      });
    }
  };

  setShowFunctionAreasStatus = (): void => {
    if (this.showFunctionAreas) {
      this.funcAreasMesh?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.add(val);
        }
      });
      this.funcAreasTextGroups?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.add(val);
        }
      });
    } else {
      this.funcAreasMesh?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
      });
      this.funcAreasTextGroups?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
      });
    }
    this.setFuncAreasTextScale();
    this.renderView();
  };

  groupAddFuncAreas = (paths: Array<LocMapElement2>): void => {
    this.clearFuncAreas();
    this.clearFuncAreasText();
    this.funcAreasMesh = createFuncAreas(paths);
    this.funcAreasTextGroups = createFuncAreasTextGroups(paths);
    this.funcAreasMesh?.forEach((val) => {
      val.renderOrder = FuncAreaOrder;
    });
    this.funcAreasTextGroups?.forEach((val) => {
      val.renderOrder = FuncAreaOrder;
    });
    this.setShowFunctionAreasStatus();
  };

  setShowVirtualWallsStatus = (): void => {
    if (this.showVirtualWalls) {
      this.virtualWallsLine?.forEach((val) => {
        if (this.glGroup) {
          val.renderOrder = VirtualWallsLineOrder;
          this.glGroup.add(val);
        }
      });
      this.accessControlLine?.forEach((val) => {
        if (this.glGroup) {
          val.renderOrder = VirtualWallsLineOrder;
          this.glGroup.add(val);
        }
      });
    } else {
      this.virtualWallsLine?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
      });
      this.accessControlLine?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
      });
    }
    this.renderView();
  };

  groupAddVirtualWalls = (paths: Array<LocMapElement2>): void => {
    this.clearVirtualWallsLine();
    this.virtualWallsLine = createVirtualWalls(paths);
    this.setShowVirtualWallsStatus();
  };

  groupAddAccessLines = (paths: Array<LocMapElement2>): void => {
    this.clearAccessControlLine();
    this.accessControlLine = createAccessLines(paths);
    this.setShowVirtualWallsStatus();
  };

  groupAddNoiseAreas = (paths: Array<LocMapElement2>): void => {
    this.clearNoiseAreas();
    this.noiseAreasMesh = createNoiseAreas(paths);
    this.noiseAreasMesh.forEach((val) => {
      if (this.glGroup) {
        this.glGroup.add(val);
      }
    });
    this.renderView();
  };

  setShowFunctionPointsStatus = (): void => {
    if (this.showFunctionPoints) {
      this.funcPointsGroup?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.add(val);
        }
      });
    } else {
      this.funcPointsGroup?.forEach((val) => {
        if (this.glGroup) {
          this.glGroup.remove(val);
        }
      });
    }
    this.setFuncPointsScale();
    this.renderView();
  };

  groupAddFuncPoint = (paths: Array<LocMapElement2>): void => {
    this.clearFuncPoints();
    if (paths?.length > 0) {
      this.funcPointsGroup = createFuncPoints(paths);
      this.funcPointsGroup.forEach((val) => {
        val.renderOrder = FuncPointRenderOrder;
      });
    }
    this.setShowFunctionPointsStatus();
  };

  showFuncPointForType = (pointModel: LocMapElement2): boolean => {
    let show = false;
    if (this.showFuncPointsType == -1) {
      show = true;
    } else if (this.showFuncPointsType == pointModel?.sub_type) {
      show = true;
    }
    return show;
  };

  setCanShowFuncPoints = (): void => {
    this.canShowFunctionPoints = new Array<LocMapElement2>();
    this.functionPoints?.forEach((val) => {
      if (this.showFuncPointForType(val)) {
        this.canShowFunctionPoints?.push(val);
      }
    });
    if (this.functionPoints) {
      this.groupAddFuncPoint(this.canShowFunctionPoints);
    }
  };

  setShowFuncPointsType = (type: number): void => {
    this.showFuncPointsType = type;
    this.setCanShowFuncPoints();
  };

  setMapFunctionPoints = (funcPoints: Array<LocMapElement2>): void => {
    this.functionPoints = funcPoints;
    this.setCanShowFuncPoints();
  };

  setFunctionAreas = (funcAreas: Array<LocMapElement2>): void => {
    this.functionAreas = funcAreas;
    this.groupAddFuncAreas(this.functionAreas);
  };

  setMapVirtualWalls = (virtualWalls: Array<LocMapElement2>): void => {
    const tempVirtualWalls = new Array<LocMapElement2>();
    const tempAccessLines = new Array<LocMapElement2>();
    virtualWalls?.forEach((val) => {
      if (val.sub_type == MapEditType.edit_func_accesscontrol_line_type_2) {
        tempAccessLines.push(val);
      } else {
        tempVirtualWalls.push(val);
      }
    });
    this.mapVirtualWalls = tempVirtualWalls;
    this.mapAccessControlLine = tempAccessLines;
    this.groupAddVirtualWalls(this.mapVirtualWalls);
    this.groupAddAccessLines(this.mapAccessControlLine);
  };

  setMapNoiseAreas = (noiseAreas: Array<LocMapElement2>): void => {
    this.mapNoiseAreas = noiseAreas;
    this.groupAddNoiseAreas(this.mapNoiseAreas);
  };

  setShowVirtualWalls = (show: boolean): void => {
    this.showVirtualWalls = show;
    this.setShowVirtualWallsStatus();
  };

  setShowFunctionPoints = (show: boolean): void => {
    this.showFunctionPoints = show;
    this.setShowFunctionPointsStatus();
  };

  setShowFunctionAreas = (show: boolean): void => {
    this.showFunctionAreas = show;
    this.setShowFunctionAreasStatus();
  };

  clearAllMapElements = (): void => {
    this.setMapVirtualWalls([]);
    this.setMapNoiseAreas([]);
    this.setMapFunctionPoints([]);
    this.setFunctionAreas([]);
  };

  setShowGriddingScale = (show: boolean): void => {
    this.showGriddingScale = show;
    this.showGriddingScaleGreate();
  };

  setGriddingScaleValue = (val: number): void => {
    this.griddingScaleValue = val;
    this.createCurrGriddingScaleGroup();
  };

  hintGriddingLineGroup = (): void => {
    if (this.griddingLineGroup && this.onlyScaleGroup) {
      this.onlyScaleGroup.remove(this.griddingLineGroup);
      this.griddingLineGroup?.traverse((val) => {
        val.clear();
      });
      this.griddingLineGroup?.clear();
    }
  };

  createCurrGriddingScaleGroup = (): void => {
    if (this.griddingScaleWidth > 0 && this.griddingScaleHeight > 0) {
      this.hintGriddingLineGroup();
      this.griddingLineGroup = null;
      this.griddingLineGroup = createGriddingScaleLine(
        this.griddingScaleValue,
        this.griddingScaleWidth,
        this.griddingScaleHeight
      );
      if (
        this.showGriddingScale &&
        this.griddingLineGroup &&
        this.onlyScaleGroup
      ) {
        this.griddingLineGroup.renderOrder = GriddingScaleOrder;
        this.onlyScaleGroup.add(this.griddingLineGroup);
        this.renderView();
      }
    } else {
      this.setGriddingScaleWH();
    }
  };

  showGriddingScaleGreate = (): void => {
    if (this.showGriddingScale) {
      if (this.griddingLineGroup && this.onlyScaleGroup) {
        this.onlyScaleGroup.add(this.griddingLineGroup);
      } else {
        this.createCurrGriddingScaleGroup();
      }
    } else {
      this.hintGriddingLineGroup();
      this.renderView();
    }
  };

  setGriddingScaleWH = (): void => {
    if (!(this.griddingScaleWidth > 0 && this.griddingScaleHeight > 0)) {
      const w = this.canvasDiv?.clientWidth as number;
      const h = this.canvasDiv?.clientHeight as number;
      this.griddingScaleWidth = w / this.scale;
      this.griddingScaleHeight = h / this.scale;
      this.showGriddingScaleGreate();
    }
  };

  setFuncPointsScale = (): void => {
    if (this.showFunctionPoints) {
      this.funcPointsGroup?.forEach((val) => {
        val.scale.setX(1 / this.scale);
        val.scale.setY(1 / this.scale);
      });
    }
  };

  setRunningTaskPath = (running: LocLaneInfo, needTopLane = false): void => {
    //可能存在异步导致的加载顺序变化从而导致上下层级变化而表现出闪的原因
    createLaneInfoMeshImg(running, (valMesh) => {
      const lastRunningPath = this.runningTaskPathsMap.get(running.lane_id);
      if (lastRunningPath) {
        this.glGroup?.remove(lastRunningPath);
        this.removeObj(lastRunningPath);
      }
      const order = parseFloat(TaskPathRenderOrder + "." + running.lane_id);
      if (needTopLane) {
        valMesh.renderOrder = TaskingPathRenderOrder;
      } else {
        valMesh.renderOrder = order;
      }
      this.runningTaskPathsMap.set(running.lane_id, valMesh);
      this.glGroup?.add(valMesh);
      this.renderView();
    });
  };

  clearRunningTaskPaths = (): void => {
    this.runningTaskPathsMap.forEach((val) => {
      this.glGroup?.remove(val);
      this.removeObj(val);
    });
    this.runningTaskPathsMap.clear();
    this.renderView();
  };

  getRunningTaskPathMapSize = (): number => {
    return this.runningTaskPathsMap.size;
  };

  checkNeedUpdate = (taskList: ChildTaskStatus[]): boolean => {
    if (taskList?.length > 0 && this.runningTaskPathsMap?.size > 0) {
      let allHave = true;
      taskList.forEach((val) => {
        if (val?.task_status) {
          let have = false;
          this.runningTaskPathsMap.forEach((value, key) => {
            if (Number(key) == val.id) {
              have = true;
            }
          });
          if (!have) {
            allHave = false;
          }
        }
      });
      return allHave;
    } else {
      return false;
    }
  };

  checkFloorNeedUpdate = (taskList: SubTask[]): boolean => {
    if (taskList?.length > 0 && this.runningTaskPathsMap?.size > 0) {
      let allHave = true;
      taskList.forEach((val) => {
        if (val?.state) {
          let have = false;
          this.runningTaskPathsMap.forEach((value, key) => {
            if (Number(key) == Number(val.id)) {
              have = true;
            }
          });
          if (!have) {
            allHave = false;
          }
        }
      });
      return allHave;
    } else {
      return false;
    }
  };

  setOnlyScaleGroupScale = (): void => {
    if (this.onlyScaleGroup) {
      this.onlyScaleGroup.scale.setX(this.scale);
      this.onlyScaleGroup.scale.setY(this.scale);
    }
  };

  memoryGlGroup = (): void => {
    this.glGroup?.traverse((obj) => {
      console.log("清理group的内存", obj.type);
      if (obj.type === "Mesh") {
        const mesh = obj as Mesh;
        mesh.geometry.dispose();
        // mesh.material.dispose();
      }
    });
    if (this.glScene && this.glGroup) {
      this.glScene.remove(this.glGroup);
    }
  };

  memoryOnlyScaleGroup = (): void => {
    this.onlyScaleGroup?.traverse((obj) => {
      if (obj.type === "Mesh") {
        const mesh = obj as Mesh;
        mesh.geometry.dispose();
        // mesh.material.dispose();
      }
    });
    if (this.glScene && this.onlyScaleGroup) {
      this.glScene.remove(this.onlyScaleGroup);
    }
  };

  memoryStaticGroup = (): void => {
    this.staticGroup?.traverse((obj) => {
      if (obj.type === "Mesh") {
        const mesh = obj as Mesh;
        mesh.geometry.dispose();
        // mesh.material.dispose();
      }
    });
    if (this.glScene && this.staticGroup) {
      this.glScene.remove(this.staticGroup);
    }
  };

  clearCache = (item: any): void => {
    item?.geometry?.dispose();
    item?.material?.dispose();
  };

  removeObj = (obj: Object3D): void => {
    const arr = obj.children.filter((x) => x);
    arr.forEach((item) => {
      if (item?.children?.length) {
        this.removeObj(item);
      } else {
        this.clearCache(item);
        item.clear();
      }
    });
    obj.clear();
  };

  clearScene = (): void => {
    if (this.glScene) {
      this.removeObj(this.glScene);
    }
  };

  memoryClear = (removeAll = false): void => {
    this.memoryGlGroup();
    this.memoryStaticGroup();
    this.memoryOnlyScaleGroup();
    if (removeAll) {
      try {
        this.clearScene();
        if (this.glScene && this.ambientLight) {
          this.glScene.remove(this.ambientLight);
          this.glScene.clear();
        }
        if (this.glRender) {
          console.log("清理three的webgl");
          this.glRender.dispose();
          this.glRender.forceContextLoss();
          const gl = this.glRender.domElement.getContext("webgl");
          if (gl) {
            const context = gl.getExtension("WEBGL_lose_context");
            if (context) {
              context.loseContext();
            }
          }
          this.glRender = null;
        }
        Cache.clear();
      } catch (e) {
        console.error("清理three的webgl异常");
      }
    }
  };

  clearLastMapPic = (): void => {
    if (this.glGroup && this.mapPicMesh) {
      this.glGroup.remove(this.mapPicMesh);
      this.mapPicMesh?.traverse((val) => {
        val?.clear();
      });
      this.mapPicMesh.geometry.dispose();
      this.mapPicMesh = null;
    }
  };

  setTranslateGroup = (): void => {
    if (this.glGroup) {
      const MTx = this.translateX;
      const MTy = -this.translateY;
      this.glGroup.position.setX(MTx);
      this.glGroup.position.setY(MTy);
    }
  };

  setScaleGroup = (): void => {
    if (this.glGroup) {
      const MTx = this.translateX;
      const MTy = -this.translateY;
      const beforeScaleTranslateX = MTx * this.scale;
      const beforeScaleTranslateY = MTy * this.scale;
      this.glGroup.position.setX(beforeScaleTranslateX);
      this.glGroup.position.setY(beforeScaleTranslateY);
      this.glGroup.scale.setX(this.scale);
      this.glGroup.scale.setY(this.scale);
    }
  };

  setRotateGroup = (): void => {
    if (this.glGroup) {
      this.glGroup.rotation.z = -(Math.PI / 180) * this.rotateAngle;
    }
  };

  removeAllElements = (): void => {
    //
  };

  onMouseClick = (event: MouseEvent): void => {
    //
  };

  renderView = (): void => {
    if (this.glRender && this.glScene && this.glCamera) {
      this.glRender.render(this.glScene, this.glCamera);
    }
  };

  setTranslate = (): void => {
    this.setTranslateGroup();
    this.setScaleGroup();
    this.renderView();
  };

  setRotateAngle = (rotateAngle: number): void => {
    this.rotateAngle = rotateAngle;
    this.setRotateGroup();
    this.renderView();
  };

  setScale = (scale: number): void => {
    this.scale = scale;
    this.setCarIconScale();
    this.setFuncPointsScale();
    this.setFuncAreasTextScale();
    this.setOnlyScaleGroupScale();
    this.setTranslate();
  };

  setTranslateVal = (translateX: number, translateY: number): void => {
    this.translateX = translateX;
    this.translateY = translateY;
    this.setTranslate();
  };

  destroyAll = (): void => {
    this.clearCarIconMesh();
    this.clearVirtualWallsLine();
    this.clearAccessControlLine();
    this.clearNoiseAreas();
    this.clearFuncAreasText();
    this.clearFuncPoints();
    this.clearRunningTaskPaths();
    this.removeAllElements();
    this.memoryClear(true);
  };
}
